import { Patient, Provenance } from '@medplum/fhirtypes'
import { useFhirSearch } from '@/hooks'
import { modals } from '@mantine/modals'
import type { AvailResourceType, AvailResource } from '@/hooks/fhirSearchConfig.ts'
import { ChartItem } from './PatientChart.utils.tsx'
import { memo, useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { useMedplum } from '@medplum/react'
import { Button, Loader, LoadingOverlay, Table } from '@mantine/core'
import { Th } from '@/components'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useScrollContext } from '@/context/ScrollContext'

type Props<T extends AvailResource> = {
  patient: Patient
  item: ChartItem
  availFields: { code: string; title: string; width?: string }[]
  renderColumn: (resource: T, itemCode: string) => string | JSX.Element
  preprocessList?: (list: T[]) => T[]
  /**
   * Rows fetched by the caller and rendered **above** the searched list, through the
   * same `renderColumn`. The Medications section uses this to union DoseSpot-synced
   * `MedicationRequest`s in beside the note-authored `MedicationStatement`s (§10.6).
   *
   * They are deliberately outside the search, and the consequences are real: they are
   * **pinned** in the order the caller supplied, so clicking a sortable column header
   * re-sorts only the searched half, and they do not page. That is the trade for a
   * bounded read-only set the server has already ordered, and for a failed fetch being
   * unable to empty the searched list beside it.
   *
   * **Only supported on a `blockRemove` table** — these rows are read-only, so the
   * table they join must not offer a remove column at all.
   */
  extraResources?: T[]
  defaultSort?: string
  blockRemove?: boolean
  includeProvenance?: boolean
  headerCellClassName?: string
}

type ResourceTableRowProps<T extends AvailResource> = {
  resource: T
  availFields: { code: string; title: string; width?: string }[]
  renderColumn: (resource: T, itemCode: string) => string | JSX.Element
  getColWidth: (itemCode?: string) => string
  blockRemove: boolean
  onRemove: (id: string) => void
}

function ResourceTableRowInner<T extends AvailResource>({
  resource,
  availFields,
  renderColumn,
  getColWidth,
  blockRemove,
  onRemove,
}: ResourceTableRowProps<T>) {
  return (
    <Table.Tr className="h-[44px]">
      {availFields.map((item) => (
        <Table.Td
          style={{ width: item.width || `${getColWidth(item.code)}%` }}
          className={`!min-w-[100px] whitespace-pre-wrap !w-[${getColWidth(item.code)}%]`}
          key={item.code}
        >
          {renderColumn(resource, item.code)}
        </Table.Td>
      ))}
      {!blockRemove && (
        <Table.Td style={{ width: `${getColWidth()}%` }} className={`w-[${getColWidth()}%]`}>
          <Button
            variant="transparent"
            className="!text-[#7D7987]"
            onClick={() =>
              modals.openConfirmModal({
                title: 'Are you sure you want to remove this resource?',
                confirmProps: { color: 'red' },
                onConfirm: () => onRemove(resource.id!),
              })
            }
          >
            Remove
          </Button>
        </Table.Td>
      )}
    </Table.Tr>
  )
}

const ResourceTableRow = memo(ResourceTableRowInner) as typeof ResourceTableRowInner

export const ResourceTable = <T extends AvailResource>({
  patient,
  item,
  defaultSort,
  availFields,
  renderColumn,
  blockRemove = false,
  includeProvenance = false,
  preprocessList,
  extraResources,
  headerCellClassName,
}: Props<T>) => {
  const [resources, setResources] = useState<T[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [initialLoaded, setInitialLoaded] = useState(false)

  const { scrollContainerId } = useScrollContext()

  const medplum = useMedplum()
  const { search, getSearchQuery, getAvailableSorting, loadMore, setSort } = useFhirSearch(
    item.resourceTypes[0] as AvailResourceType,
    50,
    defaultSort,
  )

  useEffect(() => {
    if (isLoading || !hasMore) return
    const container = scrollContainerId
      ? document.getElementById(scrollContainerId)
      : document.scrollingElement
    if (!container) return
    if (container.scrollHeight <= container.clientHeight + 50) {
      loadMore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, hasMore, isLoading, scrollContainerId])

  useEffect(() => {
    let active = true
    const searchQuery = getSearchQuery()
    if (medplum && searchQuery) {
      const resourceType = item.resourceTypes[0] as AvailResourceType
      const searchParams = `${item.relation}=Patient/${patient.id}${item.extraQuery ? `&${item.extraQuery}` : ''}${item.include ? `&_include=${resourceType}:${item.include}` : ''}`
      if (search.offset === 0) {
        setInitialLoaded(false)
      }
      setIsLoading(true)
      medplum
        .search(item.resourceTypes[0] as AvailResourceType, `${searchQuery}&${searchParams}`)
        .then(async (result) => {
          if (!active) return
          let resourceList = result.entry?.map((entry) => entry.resource as T) || []

          const provenanceItems: Provenance[] = []
          if (includeProvenance) {
            for (const resource of resourceList) {
              const exts = resource.extension?.filter((item) =>
                item.url?.match(/record-provenance/),
              )

              const existProvenances: Provenance[] = []
              const provenanceIds: string[] = []
              for (const ext of exts || []) {
                const provenanceId = ext.valueReference?.reference?.split('/').pop()

                if (provenanceId && !provenanceIds.includes(provenanceId)) {
                  const p = provenanceItems.find((item) => item.id === provenanceId)
                  if (p) {
                    existProvenances.push(p)
                  } else {
                    provenanceIds.push(provenanceId)
                  }
                }
              }

              const provenances = await medplum.search(
                'Provenance',
                `_id=${provenanceIds.join(',')}`,
              )

              const items = provenances.entry?.map((r) => r.resource as Provenance) || []

              resource.contained = [...(resource.contained || []), ...items, ...existProvenances]
              provenanceItems.push(...items)
            }
          }

          if (preprocessList) {
            resourceList = preprocessList(resourceList)
          }
          if (!active) return
          if (search.offset === 0) {
            setResources(resourceList)
          } else {
            setResources((prev) => [...prev, ...resourceList])
          }
          setHasMore(!!result.link?.find((item) => item.relation === 'next'))
        })
        .catch((e) => {
          if (!active) return
          logError('patient', 'Failed to fetch resources', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resources',
            color: 'red',
          })
        })
        .finally(() => {
          if (!active) return
          setIsLoading(false)
          if (search.offset === 0) {
            setInitialLoaded(true)
          }
        })
    } else {
      setIsLoading(false)
    }
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, search])

  const sorted = (itemCode: string) => {
    return search.sortRules?.find((item) => item.code === itemCode)
  }

  const isSortAvailable = (itemCode: string) => {
    return getAvailableSorting().includes(itemCode)
  }

  const onSort = (itemCode: string): (() => void) | undefined => {
    if (!isSortAvailable(itemCode)) {
      return undefined
    }
    const existSort = sorted(itemCode)

    if (!existSort) {
      return () => setSort(itemCode, 'desc')
    }

    if (existSort.descending) {
      return () => setSort(itemCode, 'asc')
    }

    return () => setSort(itemCode, 'none')
  }

  const removeResource = useCallback(
    async (id: string) => {
      await medplum.deleteResource(item.resourceTypes[0] as AvailResourceType, id)
      medplum.invalidateSearches(item.resourceTypes[0] as AvailResourceType)
      setResources((prev) => prev.filter((r) => r.id !== id))
    },
    [medplum, item],
  )

  const getColWidth = useCallback(
    (itemCode?: string) => {
      const columnsWithWidth = availFields.filter((item) => item.width).length
      const percent = Math.floor((columnsWithWidth / availFields.length) * 100)
      const columnWidth = percent / (!blockRemove ? columnsWithWidth + 1 : columnsWithWidth)

      if (!itemCode) {
        return `${columnWidth}%`
      }

      const field = availFields.find((item) => item.code === itemCode)
      if (field) {
        return field.width || `${columnWidth}%`
      }
      return `${columnWidth}%`
    },
    [availFields, blockRemove],
  )

  const showInitialOverlay = !initialLoaded

  return (
    <div style={{ position: 'relative', minHeight: showInitialOverlay ? 120 : undefined }}>
      <LoadingOverlay visible={showInitialOverlay} />
      <InfiniteScroll
        dataLength={resources.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<Loader color="blue" />}
        scrollableTarget={scrollContainerId || null}
        style={{ display: 'contents' }}
      >
        <Table striped>
          <Table.Thead
            // The header sticks to the TOP of its scroll container (the
            // chart-content `overflow-y-auto` div), so `top: 0` is correct here.
            // Using the context's `stickyTop` (the scrollport's VIEWPORT offset,
            // e.g. 178px on PatientPage) double-counted that offset — the header
            // pinned ~178px BELOW the scrollport's top edge, leaving the first
            // row floating above it. `position: sticky` is already relative to
            // the scrollport, so the offset must be 0. (CallPage publishes
            // `stickyTop: '0px'` for this same reason.)
            style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#FBFBFB' }}
          >
            <Table.Tr className="h-[44px] text-[#7D7987]">
              {availFields.map((item) => (
                <Th
                  style={{ width: `${getColWidth(item.code)}%` }}
                  key={item.code}
                  reversed={sorted(item.code)?.descending === true}
                  sorted={!!sorted(item.code)}
                  onSort={onSort(item.code)}
                  className={headerCellClassName}
                >
                  {item.title}
                </Th>
              ))}
              {!blockRemove && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(extraResources ?? []).map((resource) => (
              <ResourceTableRow
                key={resource.id}
                resource={resource}
                availFields={availFields}
                renderColumn={renderColumn}
                getColWidth={getColWidth}
                // Always, regardless of the table's own setting: caller-supplied rows are
                // never BeHuman's to delete — the DoseSpot ones are owned by DoseSpot and
                // replaced by every sync. Hence `extraResources`' blockRemove-only contract.
                blockRemove
                onRemove={removeResource}
              />
            ))}
            {resources.map((resource) => (
              <ResourceTableRow
                key={resource.id}
                resource={resource}
                availFields={availFields}
                renderColumn={renderColumn}
                getColWidth={getColWidth}
                blockRemove={blockRemove}
                onRemove={removeResource}
              />
            ))}
          </Table.Tbody>
        </Table>
      </InfiniteScroll>
    </div>
  )
}
